import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { patientsApi, type ClinicPatientListEntry } from '../lib/api';

const ENCOUNTER_TYPE_LABELS: Record<string, string> = {
    general: 'General',
    follow_up: 'Follow-up',
    antenatal_visit: 'Antenatal',
    postnatal_visit: 'Postnatal',
    vaccination: 'Vaccination',
    screening: 'Screening',
    emergency: 'Emergency',
};

export function PatientRecordsScreen() {
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);
    // Debounce isn't critical here — the endpoint is fast and the roster is
    // small at pilot scale. If a clinic hits thousands of patients we'll add
    // a useDebounce hook then.
    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: ['clinic-patients', search.trim(), offset],
        queryFn: () => {
            const q = search.trim();
            return patientsApi.listClinicPatients({ search: q, offset });
        },
        refetchInterval: 30000,
        refetchOnMount: 'always',
    });

    const patients = data?.patients ?? [];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-slate-900 text-2xl font-semibold">Patient records</h1>
                <p className="text-slate-500 text-sm mt-1">
                    Saved clinic cards and past visits, including profiles still being completed. Open a patient to continue registration or review history.
                </p>
            </div>

            <div className="pm-card p-4">
                <label className="pm-label" htmlFor="search">Search</label>
                <div className="flex gap-2">
                    <input
                        id="search"
                        className="pm-input flex-1"
                        type="text"
                        placeholder="Name, phone or hospital number"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
                        autoComplete="off"
                    />
                    <button
                        type="button"
                        className="pm-btn-outline whitespace-nowrap"
                        onClick={() => void refetch()}
                        disabled={isFetching}
                    >
                        {isFetching ? 'Loading…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {isLoading && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">Loading…</div>
            )}
            {isError && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">
                    Couldn’t load patient records. Try refreshing.
                </div>
            )}
            {!isLoading && !isError && patients.length === 0 && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">
                    {search.trim()
                        ? 'No patients match that search.'
                        : 'No patient records yet. Create a patient card in Reception to add them here.'}
                </div>
            )}
            {patients.length > 0 && (
                <div className="pm-card overflow-hidden">
                    <ul className="divide-y divide-slate-100">
                        {patients.map((p) => (
                            <PatientRow key={p.id} patient={p} />
                        ))}
                    </ul>
                </div>
            )}
            {(offset > 0 || data?.has_more) && <nav aria-label="Patient record pages" className="flex items-center justify-between gap-3"><button type="button" className="pm-btn-outline" disabled={offset === 0 || isFetching} onClick={() => setOffset(Math.max(0,offset-50))}>Previous</button><span className="text-sm text-slate-500">Page {offset / 50 + 1}</span><button type="button" className="pm-btn-outline" disabled={!data?.has_more || isFetching} onClick={() => setOffset(offset+50)}>Next</button></nav>}
        </div>
    );
}

function PatientRow({ patient }: { patient: ClinicPatientListEntry }) {
    const displayName = patient.full_name?.trim() || 'Unnamed patient';
    const lastVisitType = patient.last_encounter ? ENCOUNTER_TYPE_LABELS[patient.last_encounter.encounter_type]
        ?? patient.last_encounter.encounter_type : 'No visits yet';
    return (
        <li>
            <Link
                to={`/patient-records/${patient.id}`}
                className="block px-4 py-3 hover:bg-slate-50 transition"
            >
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-slate-900 font-medium truncate">{displayName}</span>
                            <PamosiBadge onPamosi={patient.on_pamosi} /><span className="text-xs text-slate-600">{patient.registration_status === 'submitted' ? 'Registration submitted' : 'Profile incomplete'}</span>
                        </div>
                        <div className="text-slate-500 text-xs mt-0.5">
                            {patient.phone_e164}{patient.hospital_numbers?.length ? ` · Hospital no. ${patient.hospital_numbers.join(', ')}` : ''}
                        </div>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="text-slate-700 text-sm">{lastVisitType}</div>
                        <div className="text-slate-400 text-xs">
                            {patient.last_encounter ? prettyDate(patient.last_encounter.started_at) : 'Card saved · open profile'}
                        </div>
                    </div>
                </div>
            </Link>
        </li>
    );
}

function PamosiBadge({ onPamosi }: { onPamosi: boolean }) {
    return (
        <span
            className={
                onPamosi
                    ? 'rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800'
                    : 'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'
            }
        >
            {onPamosi ? 'On Pamosi' : 'Not on Pamosi'}
        </span>
    );
}

function prettyDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}
